import { useEffect, useMemo, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonItem,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonLabel,
  IonNote,
  IonIcon,
  IonBackButton,
  IonButtons,
  IonToast,
  IonList,
  IonChip,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useMachine } from '@xstate/react';
import { arrowBack, arrowForward, checkmark, create, save } from 'ionicons/icons';
import { z } from 'zod';
import { jobPostingMachine, jobPostingSteps } from '@/lib/workflow/machines/jobPostingMachine';
import { workTypeEnum, TRADE_SKILLS, PROVINCES } from '@/lib/validation/registration';
import { useCreateJobListing, useMyEmployerProfile, useSessionInfo } from '@/lib/api/hooks';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';

interface Props {
  onPublished?: () => void;
}

const detailsSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  description: z.string().min(20, 'Description must be at least 20 characters'),
});

export function JobPostingWizard({ onPublished }: Props) {
  const history = useHistory();
  const [state, send] = useMachine(jobPostingMachine);
  const [form, setForm] = useState({
    title: '', description: '', requirements: '',
    province: '', city: '',
    salaryMin: '', salaryMax: '', workType: 'Contract', deadline: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  const { data: sessionEnvelope } = useSessionInfo();
  const tenantId = sessionEnvelope?.data?.currentTenant?.id ?? '';
  const { data: employerProfile } = useMyEmployerProfile();
  const createJob = useCreateJobListing();

  const value = state.value as string;

  const visibleSteps = useMemo(() => [...jobPostingSteps], []);
  const stepIndex = Math.max(0, visibleSteps.indexOf(value as typeof jobPostingSteps[number]));

  useEffect(() => {
    if (state.matches('idle')) send({ type: 'START' });
  }, [state, send]);

  useEffect(() => {
    if (state.matches('published')) {
      if (onPublished) onPublished();
      else history.push('/employer/jobs');
    }
  }, [state, onPublished]);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const handleNext = () => {
    switch (value) {
      case 'jobDetails': {
        const r = detailsSchema.safeParse({ title: form.title, description: form.description });
        if (!r.success) {
          const e: Record<string, string> = {};
          for (const issue of r.error.issues) e[String(issue.path[0])] = issue.message;
          setErrors(e); return;
        }
        setErrors({});
        send({ type: 'SUBMIT_DETAILS', title: form.title, description: form.description, requirements: form.requirements ? form.requirements.split('\n').filter(Boolean) : [] });
        break;
      }
      case 'skills':
        if (state.context.skills.length === 0) { setErrors({ skills: 'Add at least one skill' }); return; }
        setErrors({});
        send({ type: 'SUBMIT_SKILLS' });
        break;
      case 'location':
        send({ type: 'SUBMIT_LOCATION', province: form.province, city: form.city });
        break;
      case 'compensation':
        send({ type: 'SUBMIT_COMPENSATION', salaryMin: form.salaryMin, salaryMax: form.salaryMax, workType: form.workType, applicationDeadline: form.deadline });
        break;
      case 'review':
        send({ type: 'PUBLISH' });
        if (!employerProfile?._id || !tenantId) {
          setToast({ msg: 'Employer profile not found. Complete your profile first.', color: 'danger' });
          send({ type: 'PUBLISH_ERROR', message: 'Employer profile not found' });
          return;
        }
        createJob.mutate(
          {
            title: state.context.title,
            description: state.context.description,
            requirements: state.context.requirements,
            skillsRequired: state.context.skills,
            province: state.context.province,
            location: `${state.context.city}, ${state.context.province}`,
            salaryMin: state.context.salaryMin ? Number(state.context.salaryMin) : undefined,
            salaryMax: state.context.salaryMax ? Number(state.context.salaryMax) : undefined,
            workType: state.context.workType as 'Permanent' | 'Contract' | 'PartTime' | 'Freelance',
            applicationDeadline: state.context.applicationDeadline || undefined,
            employerProfile: { type: 'employerprofile', id: employerProfile._id },
            status: 'Open',
            tenant: { type: 'tenant' as const, id: tenantId },
          },
          {
            onSuccess: () => {
              send({ type: 'PUBLISHED' });
            },
            onError: (err: Error) => {
              const msg = err.message || 'Failed to publish job';
              send({ type: 'PUBLISH_ERROR', message: msg });
              setToast({ msg, color: 'danger' });
            },
          },
        );
        break;
      default: break;
    }
  };

  const toggleSkill = (s: string) => {
    send({ type: 'TOGGLE_SKILL', skill: s });
  };

  const renderStep = () => {
    switch (value) {
      case 'jobDetails':
        return (
          <IonList lines="full">
            <IonItem><IonInput label="Job title" placeholder="e.g. Emergency Electrician" value={form.title} onIonInput={(e) => set({ title: e.detail.value! })} /></IonItem>
            {errors.title && <IonNote color="danger">{errors.title}</IonNote>}
            <IonItem><IonTextarea label="Description" placeholder="Describe the role, responsibilities, and requirements" value={form.description} onIonInput={(e) => set({ description: e.detail.value! })} rows={4} /></IonItem>
            {errors.description && <IonNote color="danger">{errors.description}</IonNote>}
            <IonItem><IonTextarea label="Requirements (one per line)" value={form.requirements} onIonInput={(e) => set({ requirements: e.detail.value! })} rows={3} /></IonItem>
          </IonList>
        );
      case 'skills':
        return (
          <div className="ion-padding">
            <IonLabel style={{ fontWeight: 600 }}>Required skills</IonLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {TRADE_SKILLS.map((s) => (
                <IonChip key={s} outline={!state.context.skills.includes(s)} color={state.context.skills.includes(s) ? 'primary' : 'medium'} onClick={() => toggleSkill(s)}>
                  {state.context.skills.includes(s) && <IonIcon icon={checkmark} />}{s}
                </IonChip>
              ))}
            </div>
            {errors.skills && <IonNote color="danger">{errors.skills}</IonNote>}
          </div>
        );
      case 'location':
        return (
          <IonList lines="full">
            <IonItem><IonSelect label="Province" value={form.province} onIonChange={(e) => set({ province: e.detail.value })}>{PROVINCES.map((p) => <IonSelectOption key={p} value={p}>{p}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonInput label="City / Suburb" value={form.city} onIonInput={(e) => set({ city: e.detail.value! })} /></IonItem>
          </IonList>
        );
      case 'compensation':
        return (
          <IonList lines="full">
            <IonItem><IonInput label="Min salary (ZAR)" type="number" value={form.salaryMin} onIonInput={(e) => set({ salaryMin: e.detail.value! })} /></IonItem>
            <IonItem><IonInput label="Max salary (ZAR)" type="number" value={form.salaryMax} onIonInput={(e) => set({ salaryMax: e.detail.value! })} /></IonItem>
            <IonItem><IonSelect label="Work type" value={form.workType} onIonChange={(e) => set({ workType: e.detail.value })}>{workTypeEnum.map((w) => <IonSelectOption key={w} value={w}>{w}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonInput label="Application deadline" type="date" value={form.deadline} onIonInput={(e) => set({ deadline: e.detail.value! })} /></IonItem>
          </IonList>
        );
      case 'review':
        return (
          <IonList lines="full">
            <ReviewRow label="Title" value={state.context.title} onEdit={() => send({ type: 'EDIT_STEP', step: 'details' })} />
            <ReviewRow label="Description" value={state.context.description.slice(0, 80) + '…'} onEdit={() => send({ type: 'EDIT_STEP', step: 'details' })} />
            <ReviewRow label="Skills" value={state.context.skills.join(', ')} onEdit={() => send({ type: 'EDIT_STEP', step: 'skills' })} />
            <ReviewRow label="Location" value={`${state.context.city}, ${state.context.province}`} onEdit={() => send({ type: 'EDIT_STEP', step: 'location' })} />
            <ReviewRow label="Compensation" value={`R${state.context.salaryMin || '0'} – R${state.context.salaryMax || '0'}/${state.context.workType}`} onEdit={() => send({ type: 'EDIT_STEP', step: 'compensation' })} />
          </IonList>
        );
      case 'publishing':
        return <div className="bp-text-center ion-padding"><IonSpinner /><p>Publishing…</p></div>;
      case 'published':
        return (
          <div className="bp-text-center ion-padding">
            <IonIcon icon={checkmark} color="success" style={{ fontSize: '3rem' }} />
            <h2 className="bp-heading">Job published!</h2>
            <IonText color="medium">Your listing is now visible to candidates.</IonText>
            <IonButton expand="block" className="ion-margin-top" onClick={() => onPublished ? onPublished() : history.push('/employer/jobs')}>Back to jobs</IonButton>
          </div>
        );
      default: return null;
    }
  };

  const showNav = !['idle', 'publishing', 'published'].includes(value);

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/employer" /></IonButtons>
          <IonTitle>Post a job</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {showNav && (
          <div className="bp-stepper ion-padding-bottom">
            {visibleSteps.map((_, i) => <div key={i} className={`bp-step-dot ${i <= stepIndex ? 'active' : ''} ${i === stepIndex ? 'current' : ''}`} />)}
            <IonLabel color="medium" className="bp-step-label">Step {stepIndex + 1} of {visibleSteps.length}</IonLabel>
          </div>
        )}
        {renderStep()}
            <AppFooter />

      </IonContent>
      {showNav && (
        <div className="bp-wizard-footer">
          <IonButton fill="clear" onClick={() => send({ type: 'BACK' })} disabled={value === 'jobDetails'}><IonIcon icon={arrowBack} slot="start" /> Back</IonButton>
          {value === 'review' && <IonButton fill="clear" onClick={() => send({ type: 'SAVE_DRAFT' })}><IonIcon icon={save} slot="start" /> Draft</IonButton>}
          <IonButton onClick={handleNext}>{value === 'review' ? 'Publish' : 'Next'}{value !== 'review' && <IonIcon icon={arrowForward} slot="end" />}</IonButton>
        </div>
      )}
      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2200} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}

function ReviewRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <IonItem>
      <IonLabel><div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{label}</div><div>{value}</div></IonLabel>
      <IonButton fill="clear" slot="end" onClick={onEdit}><IonIcon icon={create} /></IonButton>
    </IonItem>
  );
}
